import { useState, useEffect } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RefreshCw, Paperclip, Pencil, Trash2 } from "lucide-react";

function fmtSize(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

export default function FilesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [name, setName] = useState("");

  const load = () => { setLoading(true); api.get("/admin/attachment-library").then((r) => setRows(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const doRename = async () => {
    try {
      const { data } = await api.post("/admin/library/rename", { storage_path: edit.storage_path, filename: edit.filename, new_filename: name });
      toast.success(`Renamed across ${data.modified} product${data.modified === 1 ? "" : "s"}`);
      setEdit(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const doDelete = async (it) => {
    if (!window.confirm(`Remove "${it.filename}" from all ${it.used_by} product(s)?`)) return;
    try {
      const { data } = await api.post("/admin/library/delete", { storage_path: it.storage_path, filename: it.filename });
      toast.success(`Removed from ${data.modified} product${data.modified === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <header className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">File Library</h1>
          <p className="text-slate-500 mt-1">Every uploaded document & image. Rename or remove a file across all products at once.</p>
        </div>
        <Button variant="outline" className="rounded-sm" onClick={load} data-testid="refresh-files"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
      </header>
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-4 py-3">File</th><th className="text-left px-4 py-3">Used by</th><th className="text-left px-4 py-3">Size</th><th className="text-right px-4 py-3">Actions</th></tr>
          </thead>
          <tbody data-testid="files-table">
            {loading && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-slate-400">No files uploaded yet.</td></tr>}
            {rows.map((it, i) => (
              <tr key={it.ref_id || it.storage_path} className="border-t border-slate-100 even:bg-slate-50/50 hover:bg-blue-50/40 transition-colors">
                <td className="px-4 py-2.5"><span className="inline-flex items-center gap-2"><Paperclip className="h-4 w-4 text-slate-400" />{it.filename}</span></td>
                <td className="px-4 py-2.5 text-slate-600">{it.used_by} SKU{it.used_by === 1 ? "" : "s"}</td>
                <td className="px-4 py-2.5 text-slate-500 mono-nums">{fmtSize(it.size)}</td>
                <td className="px-4 py-2.5 text-right">
                  <Button size="sm" variant="ghost" className="rounded-sm h-8" onClick={() => { setEdit(it); setName(it.filename); }} data-testid={`file-rename-${i}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="rounded-sm h-8 text-red-500 hover:text-red-700" onClick={() => doDelete(it)} data-testid={`file-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="rounded-sm" data-testid="file-rename-dialog">
          <DialogHeader><DialogTitle className="font-heading">Rename file everywhere</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Rename this file across all products that use it.</DialogDescription>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="file-rename-input" />
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setEdit(null)}>Cancel</Button>
            <Button className="rounded-sm bg-slate-900" onClick={doRename} data-testid="file-rename-confirm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
